import { Checkbox, Radio } from "@caspeco/casper-ui-library";
import AmountSelector from "components/selection-components/preorder/widgets/amount-choice-widget";
import { ErrorType } from "components/selection-components/preorder/widgets/error-type";
import { warn } from "logger/logger";
import { ArticleStock } from "types/article-stock";
import { MenuGroup, MenuGroupType } from "types/menu-group";
import { MenuItemWebFlags } from "types/menu-item";

export interface WidgetContext {
    menuGroup: MenuGroup;
    guests?: number;
    totalGroupQuantities: number;
    currentQuantity: number;
    allQuantities: number[];
    setQuantity: (quantity: number, resetMessage: boolean) => void;
    onErrorChange: (isError: boolean, message: string) => void;
    addItemButtonText: string;
    itemFlag: MenuItemWebFlags;
    setQuantitySingelChoice: () => void;
    t?: (key: string, options?: Record<string, unknown>) => string;
    articleId?: number;
    articleStocks?: Map<number, ArticleStock[]>;
}

type WidgetFactoryFunction = (context: WidgetContext, itemIndex: number) => JSX.Element | null;

interface WidgetFactory {
    render: WidgetFactoryFunction;
}

export function createWidget(
    groupType: MenuGroupType,
    context: WidgetContext,
    itemIndex: number
): JSX.Element | null {
    const factory = widgetFactories[groupType];
    if (!factory) {
        warn(`No widget factory found for group type: ${groupType}`);
        return null;
    }

    return factory.render(context, itemIndex);
}

/**
 * Widget factory for ViewOnly group type
 */
const createViewOnlyWidget: WidgetFactory = {
    render: () => null,
};

/**
 * Widget factory for SingleChoice group type
 */
const createSingleChoiceWidget: WidgetFactory = {
    render: (context, idx) => {
        const { setQuantitySingelChoice } = context;
        return (
            <Radio
                value={String(idx)}
                onChange={() => setQuantitySingelChoice()}
                data-track-click="preorder-item-radio"
            />
        );
    },
};

/**
 * Allows selecting any quantity without constraints
 */
const createAmountChoiceWidget: WidgetFactory = {
    render: (context) => {
        const {
            currentQuantity,
            totalGroupQuantities,
            setQuantity,
            addItemButtonText,
            onErrorChange,
            articleId,
            articleStocks,
            t,
        } = context;

        // Check stock availability
        let maxStock: number | undefined;

        if (articleId && articleStocks) {
            const stocksForArticle = articleStocks.get(articleId);
            const articleStock = stocksForArticle?.[0];

            if (articleStock) {
                maxStock = articleStock.quantity;
            } else {
                // No stock info available, use fallback of 500
                maxStock = 500;
            }
        }

        const customConstraintCheck = (action: string) => {
            if (!articleStocks || !articleId)
                return { isValid: true, message: "", errorType: ErrorType.None };
            const stocksForArticle = articleStocks.get(articleId);
            const articleStock = stocksForArticle?.[0];

            const newQuantity = action === "add" ? currentQuantity + 1 : currentQuantity - 1;

            if (articleStock?.quantity && articleStock?.quantity < newQuantity) {
                return {
                    isValid: false,
                    message: t
                        ? t("preorder.menugroup.maxStock", { maxStock })
                        : `Max ${maxStock} items available`,
                    errorType: ErrorType.Maximum,
                };
            }

            if (articleStock?.quantity === 0 && newQuantity >= 0) {
                return {
                    isValid: false,
                    message: t
                        ? t("preorder.menugroup.outOfStock")
                        : `Max ${maxStock} items available`,
                    errorType: ErrorType.Maximum,
                };
            }

            return { isValid: true, message: "", errorType: ErrorType.None };
        };

        return (
            <AmountSelector
                currentQuantity={currentQuantity}
                totalGroupQuantities={totalGroupQuantities}
                setQuantity={setQuantity}
                addItemButtonText={addItemButtonText}
                maximumQuantity={maxStock}
                maxWarning=""
                onErrorChange={(isError, message) => onErrorChange(isError, message)}
                customConstraintCheck={customConstraintCheck}
                hasLoadedStocks={Boolean(articleStocks)}
                blockAdd={Boolean(maxStock && currentQuantity >= maxStock)}
            />
        );
    },
};

/**
 * Limits so the user is only allowed to make choices from menuGroup.choices different group items
 */
const createLimitedChoicesWidget: WidgetFactory = {
    render: (context) => {
        const {
            menuGroup,
            currentQuantity,
            allQuantities,
            setQuantity,
            addItemButtonText,
            onErrorChange,
            t,
        } = context;

        const selectedItemsCount = allQuantities.filter((quantity) => quantity > 0).length;
        const canAdd = currentQuantity > 0 || selectedItemsCount < menuGroup.choices;

        return (
            <AmountSelector
                setQuantity={setQuantity}
                totalGroupQuantities={selectedItemsCount}
                currentQuantity={currentQuantity}
                addItemButtonText={addItemButtonText}
                disableAdd={!canAdd}
                onErrorChange={(isError, message) => onErrorChange(isError, message)}
                maxWarning={
                    t
                        ? t("preorder.menugroup.limitedChoicesMaxWarning", {
                              choices: menuGroup.choices,
                          })
                        : ""
                }
            />
        );
    },
};

/**
 * Widget factory for LimitedMinChoicesToGuests group type
 * Requires a minimum number of selections based on guest count
 */
const createLimitedMinChoicesToGuestsWidget: WidgetFactory = {
    render: (context) => {
        const {
            guests,
            currentQuantity,
            totalGroupQuantities,
            setQuantity,
            addItemButtonText,
            onErrorChange,
            t,
        } = context;

        const minimumConstraint = guests || 1;
        const minWarning = t
            ? t("preorder.menugroup.limitedMinChoicesToGuestsMinWarning", {
                  minimumConstraint,
              })
            : `Minimum ${minimumConstraint} items must be selected`;
        return (
            <AmountSelector
                setQuantity={setQuantity}
                totalGroupQuantities={totalGroupQuantities}
                currentQuantity={currentQuantity}
                addItemButtonText={addItemButtonText}
                minimumQuantity={minimumConstraint}
                onErrorChange={(isError, message) => onErrorChange(isError, message)}
                minWarning={minWarning}
            />
        );
    },
};

/**
 * Widget factory for LimitedMinMaxChoicesToGuests group type
 * Enforces that at least one option is selected for each guest and from maximum choices alternatives.
 */
const createLimitedMinMaxChoicesToGuestsWidget: WidgetFactory = {
    render: (context) => {
        const {
            menuGroup,
            currentQuantity,
            allQuantities,
            totalGroupQuantities,
            setQuantity,
            addItemButtonText,
            onErrorChange,
        } = context;
        const selectedItemsCount = allQuantities.filter((quantity) => quantity > 0).length;
        const canAdd = currentQuantity > 0 || selectedItemsCount < menuGroup.choices;

        return (
            <AmountSelector
                setQuantity={setQuantity}
                totalGroupQuantities={totalGroupQuantities}
                currentQuantity={currentQuantity}
                addItemButtonText={addItemButtonText}
                onErrorChange={(isError, message) => onErrorChange(isError, message)}
                disableAdd={!canAdd}
            />
        );
    },
};

/**
 * Widget factory for LimitedToGuests group type
 */
const createLimitedToGuestsWidget: WidgetFactory = {
    render: (context) => {
        const {
            guests,
            currentQuantity,
            totalGroupQuantities,
            setQuantity,
            addItemButtonText,
            onErrorChange,
            t,
        } = context;

        const guestCount = guests || 1;
        const maxWarning = t
            ? t("preorder.menugroup.limitedToGuestsWarning", {
                  guests: guestCount,
              })
            : `${guestCount} items can be selected (one per guest)`;

        return (
            <AmountSelector
                setQuantity={setQuantity}
                totalGroupQuantities={totalGroupQuantities}
                currentQuantity={currentQuantity}
                addItemButtonText={addItemButtonText}
                maximumQuantity={guestCount}
                onErrorChange={(isError, message) => onErrorChange(isError, message)}
                maxWarning={maxWarning}
                disableAdd={totalGroupQuantities >= guestCount}
                blockAdd={totalGroupQuantities >= guestCount}
            />
        );
    },
};

/**
 * Widget factory for LimitedToGuestsAndChoices group type
 * The customer have to have the same amount of items as guests but can only choose from choices type of items.
 */
const createLimitedToGuestsAndChoicesWidget: WidgetFactory = {
    render: (context) => {
        const {
            menuGroup,
            guests,
            currentQuantity,
            totalGroupQuantities,
            allQuantities,
            setQuantity,
            addItemButtonText,
            onErrorChange,
            t,
        } = context;

        const guestCount = guests || 1;
        const selectedItemsCount = allQuantities.filter((quantity) => quantity > 0).length;
        const canTakeFromMoreItemTypes = selectedItemsCount < menuGroup.choices;
        const canAddMore = totalGroupQuantities < guestCount;
        const canAdd = canTakeFromMoreItemTypes && canAddMore;
        const maxWarning = t
            ? t("preorder.menugroup.limitedToGuestsAndChoicesWarning", { guests: guestCount })
            : `Maximum ${guestCount} items can be selected (one per guest)`;

        return (
            <AmountSelector
                setQuantity={setQuantity}
                totalGroupQuantities={totalGroupQuantities}
                currentQuantity={currentQuantity}
                addItemButtonText={addItemButtonText}
                maximumQuantity={guestCount}
                onErrorChange={(isError, message) => onErrorChange(isError, message)}
                maxWarning={maxWarning}
                disableAdd={!canAdd}
                blockAdd={totalGroupQuantities >= guestCount}
            />
        );
    },
};

/**
 * Widget factory for IncludedPerGuest group type.
 * Quantity is automatically set to the number of guests and cannot be changed.
 */
const createIncludedPerGuestWidget: WidgetFactory = {
    render: (context) => {
        const { guests } = context;
        const guestCount = guests || 1;

        return (
            <AmountSelector
                currentQuantity={guestCount}
                totalGroupQuantities={guestCount}
                setQuantity={() => {}}
                addItemButtonText=""
                onErrorChange={() => {}}
                disabled={true}
            />
        );
    },
};

/**
 * Implementation depends on specific requirements
 */
const createMultipleChoicesWidget: WidgetFactory = {
    render: (context) => {
        const { setQuantity } = context;

        return (
            <Checkbox
                onChange={(event) => setQuantity(event.currentTarget.checked ? 1 : 0, true)}
                data-track-click="preorder-item-checkbox"
            />
        );
    },
};

const widgetFactories: Record<MenuGroupType, WidgetFactory> = {
    [MenuGroupType.ViewOnly]: createViewOnlyWidget,
    [MenuGroupType.SingleChoice]: createSingleChoiceWidget,
    [MenuGroupType.AmountChoice]: createAmountChoiceWidget,
    [MenuGroupType.LimitedChoices]: createLimitedChoicesWidget,
    [MenuGroupType.LimitedMinChoicesToGuests]: createLimitedMinChoicesToGuestsWidget,
    [MenuGroupType.LimitedMinMaxChoicesToGuests]: createLimitedMinMaxChoicesToGuestsWidget,
    [MenuGroupType.LimitedToGuests]: createLimitedToGuestsWidget,
    [MenuGroupType.LimitedToGuestsAndChoices]: createLimitedToGuestsAndChoicesWidget,
    [MenuGroupType.MultipleChoices]: createMultipleChoicesWidget,
    [MenuGroupType.IncludedPerGuest]: createIncludedPerGuestWidget,
};
